import { html, render } from 'lit-html';
import { appStore, actions } from '../store/appStore.js';
import { authService } from '@thistle/mainframe-auth';
import userIcon from '../icons/user.svg';

export class NavigationMenu extends HTMLElement {
  private unsubscribe?: () => void;
  private lastMenuOpen: boolean | null = null;
  private lastIsAuthenticated: boolean | null = null;

  connectedCallback() {
    this.unsubscribe = appStore.subscribe(() => {
      const state = appStore.getState();
      if (state.isMenuOpen !== this.lastMenuOpen || state.isAuthenticated !== this.lastIsAuthenticated) {
        this.lastMenuOpen = state.isMenuOpen;
        this.lastIsAuthenticated = state.isAuthenticated;
        this.render();
      }
    });

    // Initialize state tracking
    const state = appStore.getState();
    this.lastMenuOpen = state.isMenuOpen;
    this.lastIsAuthenticated = state.isAuthenticated;

    this.render();

    // Close menu when clicking outside
    document.addEventListener('click', this.handleOutsideClick);
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    document.removeEventListener('click', this.handleOutsideClick);
  }

  private handleOutsideClick = (event: Event) => {
    const target = event.target as Element;
    const state = appStore.getState();

    // Only handle outside clicks if the menu is actually open
    if (state.isMenuOpen && !this.contains(target) && !target.closest('button[data-menu-toggle]')) {
      actions.closeMenu();
    }
  };

  private handleSignoutClick = () => {
    // Close menu first
    actions.closeMenu();
    // Sign out user
    authService.signOut();
  };

  private render() {
    const state = appStore.getState();
    const { isMenuOpen, isAuthenticated } = state;
    const user = authService.getUser();

    const template = html`
      <div class="relative">
        <!-- Backdrop for mobile -->
        ${isMenuOpen
          ? html`
              <div
                class="fixed inset-0 bg-black bg-opacity-25 z-40 md:hidden"
                @click=${() => actions.closeMenu()}
              ></div>
            `
          : ""}

        <!-- Menu container -->
        <div
          class="${isMenuOpen
            ? "block"
            : "hidden"} absolute left-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50 md:w-56"
        >
          ${user
            ? html`
                <!-- User profile section -->
                <div class="p-3 border-b border-gray-200">
                  <div class="flex items-center space-x-3">
                    <img
                      src="${user.picture ? user.picture : userIcon}"
                      class="w-8 h-8 rounded-full"
                    />
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-gray-900 truncate">
                        ${user.name}
                      </p>
                      <p class="text-xs text-gray-500 truncate">
                        ${user.email}
                      </p>
                    </div>
                  </div>
                </div>
              `
            : ""}

          <div class="p-2">
            <!-- Menu items -->
            ${isAuthenticated
              ? html`
                  <div class="space-y-1">
                    <button
                      class="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
                      @click=${this.handleSignoutClick}
                    >
                      Sign out
                    </button>
                  </div>
                `
              : ""}
          </div>
        </div>
      </div>
    `;

    render(template, this);
  }
}

customElements.define('navigation-menu', NavigationMenu);
